import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
    Box,
    Checkbox,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerConfigEventSettingsGroup, EventSettingsGroup } from '../../../backendsdk';
import { TrackedButton as Button, TrackedDialog as Dialog } from '../../../components/TrackedComponents';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import { COMBINED_EVENT_TYPE_MAP } from '../Event/Defs';

interface EditEventSettingsModalProps {
    customerId: number;
    customerName: string;
    currentSettings: { [key: string]: CustomerConfigEventSettingsGroup } | null | undefined;
    defaultSettings: { [key: string]: CustomerConfigEventSettingsGroup } | null | undefined;
    onClose: () => void;
    onSuccess: () => void;
}

// Mapping of single events to their recurring counterparts
const SINGLE_TO_RECURRING_EVENT_MAP: { [key: string]: string } = {
    MsgEyesOffRoad: 'MsgRecurringEOR',
    MsgTailgatingDetection: 'MsgRecurringTG',
};

const EditEventSettingsModal: React.FC<EditEventSettingsModalProps> = (props) => {
    const { controlApi } = useApi();
    const { t } = useTranslation();
    const [alertElement, setAlert] = useAlert();
    const [settings, setSettings] = useState<{ [key: string]: CustomerConfigEventSettingsGroup }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [customerId, setCustomerId] = useState<number>(props.customerId);
    const [isLoadingCustomerId, setIsLoadingCustomerId] = useState(props.customerId === 0);
    const [configurableEvents, setConfigurableEvents] = useState<string[]>([]);
    const [isLoadingConfigurableEvents, setIsLoadingConfigurableEvents] = useState(true);

    // Fetch configurable events list from API
    useEffect(() => {
        setIsLoadingConfigurableEvents(true);
        controlApi
            .controlV0ConfigurableEventsGet()
            .then((res) => {
                setConfigurableEvents(res.data || []);
            })
            .catch((err) => {
                setAlert({
                    message: err.response?.data?.error || err.message || 'Failed to fetch configurable events',
                    type: 'error',
                    duration: 6000,
                });
            })
            .finally(() => {
                setIsLoadingConfigurableEvents(false);
            });
    }, [controlApi, setAlert]);

    // Get customer ID from EventSettingsGroup endpoint if not provided
    useEffect(() => {
        if (customerId === 0 && props.customerName) {
            setIsLoadingCustomerId(true);
            controlApi
                .controlV0EventSettingsGet()
                .then((res) => {
                    const eventSettingsGroup = res.data.find(
                        (group: EventSettingsGroup) => group.customer === props.customerName,
                    );
                    if (eventSettingsGroup && eventSettingsGroup.customer_id) {
                        setCustomerId(eventSettingsGroup.customer_id);
                    } else {
                        setAlert({
                            message: `Customer ID not found for ${props.customerName}. Please ensure the customer exists.`,
                            type: 'error',
                            duration: 6000,
                        });
                    }
                })
                .catch((err) => {
                    setAlert({
                        message: err.response?.data?.error || err.message || 'Failed to fetch customer ID',
                        type: 'error',
                        duration: 6000,
                    });
                })
                .finally(() => {
                    setIsLoadingCustomerId(false);
                });
        }
    }, [customerId, props.customerName, controlApi, setAlert]);

    // Initialize settings from current or default
    useEffect(() => {
        if (props.currentSettings) {
            setSettings(JSON.parse(JSON.stringify(props.currentSettings)));
        } else if (props.defaultSettings) {
            setSettings(JSON.parse(JSON.stringify(props.defaultSettings)));
        }
    }, [props.currentSettings, props.defaultSettings]);

    // Get all event types from default settings, sorted with configurable events first
    const allEventTypes = useMemo(() => {
        if (!props.defaultSettings) {
            return [];
        }

        const eventTypes = Object.keys(props.defaultSettings);
        const configurableSet = new Set(configurableEvents);

        // Split into configurable and unconfigurable
        const configurable = eventTypes.filter((et) => configurableSet.has(et)).sort();
        const unconfigurable = eventTypes.filter((et) => !configurableSet.has(et)).sort();

        // Return configurable first, then unconfigurable
        return [...configurable, ...unconfigurable];
    }, [props.defaultSettings, configurableEvents]);

    // Check if settings have changed (only for configurable events)
    const hasChanges = useMemo(() => {
        if (!props.currentSettings && !props.defaultSettings) {
            return false;
        }
        const originalSettings = props.currentSettings || props.defaultSettings || {};

        // Compare only configurable events
        const originalConfigurable: { [key: string]: CustomerConfigEventSettingsGroup } = {};
        const currentConfigurable: { [key: string]: CustomerConfigEventSettingsGroup } = {};

        configurableEvents.forEach((eventType) => {
            if (originalSettings[eventType]) {
                originalConfigurable[eventType] = originalSettings[eventType];
            }
            if (settings[eventType]) {
                currentConfigurable[eventType] = settings[eventType];
            }
        });

        return JSON.stringify(currentConfigurable) !== JSON.stringify(originalConfigurable);
    }, [settings, props.currentSettings, props.defaultSettings]);

    const differentFromDefault = JSON.stringify(settings) !== JSON.stringify(props.defaultSettings);

    const handleSettingChange = (eventType: string, key: keyof CustomerConfigEventSettingsGroup, value: boolean) => {
        setSettings((prev) => {
            const newSettings = { ...prev };
            if (!newSettings[eventType]) {
                // Initialize with default if not exists
                newSettings[eventType] = props.defaultSettings?.[eventType] || {
                    enabled: false,
                    is_operational: false,
                    visible_to_customer: false,
                    vocal_alert_enabled: false,
                    impacts_safety_score: false,
                };
            }
            newSettings[eventType] = {
                ...newSettings[eventType],
                [key]: value,
            };
            return newSettings;
        });
    };

    const handleSend = () => {
        if (customerId === 0) {
            setAlert({ message: 'Customer ID is required', type: 'error', duration: 6000 });
            return;
        }

        // Validate: recurring events cannot be enabled if their single event is disabled
        const validationErrors: string[] = [];

        Object.entries(SINGLE_TO_RECURRING_EVENT_MAP).forEach(([singleEvent, recurringEvent]) => {
            const singleEventSettings = settings[singleEvent];
            const recurringEventSettings = settings[recurringEvent];

            // Check if single event is not alerting the driver
            // Use default settings if not in current settings
            const singleEventVocalAlertEnabled =
                singleEventSettings?.vocal_alert_enabled ??
                props.defaultSettings?.[singleEvent]?.vocal_alert_enabled ??
                false;

            // Check if recurring event is enabled
            const recurringEventEnabled =
                recurringEventSettings?.enabled ?? props.defaultSettings?.[recurringEvent]?.enabled ?? false;

            if (!singleEventVocalAlertEnabled && recurringEventEnabled) {
                validationErrors.push(`${recurringEvent} cannot be enabled when ${singleEvent} is disabled`);
            }
        });

        if (validationErrors.length > 0) {
            const errorMessage = `Invalid configuration:\n\n${validationErrors.join(
                '\n',
            )}\n\nPlease disable the recurring events or enable their corresponding single events.`;
            setAlert({ message: errorMessage, type: 'error', duration: 10000 });
            return;
        }

        setIsLoading(true);
        // Filter settings to only include configurable events
        const configurableSettings: { [key: string]: CustomerConfigEventSettingsGroup } = {};
        configurableEvents.forEach((eventType) => {
            if (settings[eventType]) {
                configurableSettings[eventType] = settings[eventType];
            }
        });

        controlApi
            .controlV0EventSettingsPost({
                eventSettingsGroupRequest: {
                    customer_id: customerId,
                    settings: JSON.stringify(configurableSettings),
                },
            })
            .then(() => {
                setAlert({ message: 'Event settings updated successfully', type: 'success', duration: 6000 });
                props.onSuccess();
                props.onClose();
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.error || err.message || 'Failed to update event settings';
                setAlert({ message: errorMessage, type: 'error', duration: 6000 });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const isConfigurable = (eventType: string) => {
        return configurableEvents.includes(eventType);
    };

    const getEventSetting = (eventType: string, key: keyof CustomerConfigEventSettingsGroup): boolean => {
        if (settings[eventType]) {
            return settings[eventType][key];
        }
        // Fallback to default if not in current settings
        return props.defaultSettings?.[eventType]?.[key] || false;
    };

    // Check if an event's settings differ from default
    const getEventDiff = (eventType: string): boolean => {
        if (!props.defaultSettings || !props.defaultSettings[eventType]) {
            return false;
        }

        const defaultEvent = props.defaultSettings[eventType];
        const currentEvent = settings[eventType] || defaultEvent;

        const keys: Array<keyof CustomerConfigEventSettingsGroup> = [
            'enabled',
            'is_operational',
            'visible_to_customer',
            'vocal_alert_enabled',
            'impacts_safety_score',
        ];

        return keys.some((key) => defaultEvent[key] !== currentEvent[key]);
    };

    const handleResetToDefault = () => {
        if (props.defaultSettings) {
            setSettings(JSON.parse(JSON.stringify(props.defaultSettings)));
        }
    };

    return (
        <Dialog
            id="edit-event-settings-dialog"
            open={true}
            maxWidth="lg"
            fullWidth
            onClose={props.onClose}
            sx={{ mx: 1 }}
        >
            <Box sx={{ backgroundColor: 'secondary.main', p: 1 }}>
                <Typography variant="h6">Edit Event Settings - {props.customerName}</Typography>
            </Box>
            <Box sx={{ p: 2, height: '80vh', display: 'flex', flexDirection: 'column' }}>
                {alertElement}
                {isLoadingCustomerId || isLoadingConfigurableEvents ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 2, alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Setting</TableCell>
                                    <TableCell align="center">enabled</TableCell>
                                    <TableCell align="center">impacts_safety_score</TableCell>
                                    <TableCell align="center">is_operational</TableCell>
                                    <TableCell align="center">visible_to_customer</TableCell>
                                    <TableCell align="center">vocal_alert_enabled</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allEventTypes.map((eventType) => {
                                    const configurable = isConfigurable(eventType);
                                    const hasDiff = getEventDiff(eventType);
                                    const eventName = COMBINED_EVENT_TYPE_MAP[eventType]
                                        ? t(COMBINED_EVENT_TYPE_MAP[eventType])
                                        : eventType;
                                    return (
                                        <TableRow
                                            key={eventType}
                                            sx={{
                                                backgroundColor: hasDiff ? 'rgba(255, 193, 7, 0.1)' : 'transparent',
                                            }}
                                        >
                                            <TableCell>
                                                <Tooltip title={eventType}>
                                                    <Typography
                                                        width="100%"
                                                        variant="body2"
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        {hasDiff && <ErrorOutlineIcon sx={{ fontSize: '1.5em' }} />}
                                                        {eventName}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={getEventSetting(eventType, 'enabled')}
                                                    disabled={!configurable}
                                                    onChange={(e) =>
                                                        handleSettingChange(eventType, 'enabled', e.target.checked)
                                                    }
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={getEventSetting(eventType, 'impacts_safety_score')}
                                                    disabled={!configurable}
                                                    onChange={(e) =>
                                                        handleSettingChange(
                                                            eventType,
                                                            'impacts_safety_score',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={getEventSetting(eventType, 'is_operational')}
                                                    disabled={!configurable}
                                                    onChange={(e) =>
                                                        handleSettingChange(
                                                            eventType,
                                                            'is_operational',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={getEventSetting(eventType, 'visible_to_customer')}
                                                    disabled={!configurable}
                                                    onChange={(e) =>
                                                        handleSettingChange(
                                                            eventType,
                                                            'visible_to_customer',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={getEventSetting(eventType, 'vocal_alert_enabled')}
                                                    disabled={!configurable}
                                                    onChange={(e) =>
                                                        handleSettingChange(
                                                            eventType,
                                                            'vocal_alert_enabled',
                                                            e.target.checked,
                                                        )
                                                    }
                                                    sx={{ width: 20, height: 20 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                    <Button id="btn-cancel" color="primary" variant="contained" onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button
                        id="btn-align-baseline"
                        color="error"
                        variant="contained"
                        onClick={handleResetToDefault}
                        disabled={!differentFromDefault || isLoadingCustomerId || isLoadingConfigurableEvents}
                    >
                        Reset to Default
                    </Button>
                    <Button
                        id="btn-send"
                        color="secondary"
                        variant="contained"
                        onClick={handleSend}
                        disabled={
                            !hasChanges ||
                            isLoading ||
                            isLoadingCustomerId ||
                            isLoadingConfigurableEvents ||
                            customerId === 0
                        }
                    >
                        {isLoading ? 'Sending...' : 'Send'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default EditEventSettingsModal;
